'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { ImportedMoyen } from '@/types/moyen';
import { useProject } from './ProjectContext';
import { computeMissingFields } from '@/utils/computeMissingFields';

type PendingMoyensContextType = {
  pendingMoyens: ImportedMoyen[];
  setPendingMoyens: React.Dispatch<React.SetStateAction<ImportedMoyen[]>>;
  pendingCount: number;
  refreshPending: () => Promise<void>;
};

const PendingMoyensContext = createContext<PendingMoyensContextType>({
  pendingMoyens: [],
  setPendingMoyens: () => {},
  pendingCount: 0,
  refreshPending: async () => {},
});

export const PendingMoyensProvider = ({ children }: { children: React.ReactNode }) => {
  const { project } = useProject();
  const [pendingMoyens, setPendingMoyens] = useState<ImportedMoyen[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = async () => {
    try {
      const res = await fetch('/api/moyens_provisoires');
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data)) return;

        const moyens: ImportedMoyen[] = data.map((m: ImportedMoyen) => {
        const missingFields = computeMissingFields(m);
        const status: 'complete' | 'incomplete' =
            missingFields.length === 0 ? 'complete' : 'incomplete';

        return {
            ...m,
            missingFields,
            status,
        };
        });

        // On ne garde que les moyens incomplets du projet sélectionné
        const filtered: ImportedMoyen[] = project?.id
        ? moyens.filter((m) => m.projetId === project.id && m.status === 'incomplete')
        : moyens.filter((m) => m.status === 'incomplete');

        setPendingMoyens(filtered);
        setPendingCount(filtered.length);
    } catch (err) {
      console.error('Erreur lors du refresh des moyens à traiter:', err);
    }
  };

  // Actualisation à chaque changement de projet
  useEffect(() => {
    refreshPending();
  }, [project]);

  return (
    <PendingMoyensContext.Provider value={{ pendingMoyens, setPendingMoyens, pendingCount, refreshPending }}>
      {children}
    </PendingMoyensContext.Provider>
  );
};

export const usePendingMoyens = () => useContext(PendingMoyensContext);
